import { useEffect, useLayoutEffect, useRef } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

const KEY = "kj_scroll_positions";

function readPositions() {
  try { return JSON.parse(sessionStorage.getItem(KEY) || "{}"); } catch { return {}; }
}
function savePosition(key, y) {
  try {
    const all = readPositions();
    all[key] = y;
    sessionStorage.setItem(KEY, JSON.stringify(all));
  } catch { /* storage blocked: Back will open at the top instead */ }
}

/** Retry briefly while the page loads its content, then settle. */
function scrollWhenReady(getTarget) {
  let tries = 0;
  const tick = () => {
    const target = getTarget();
    const max = document.documentElement.scrollHeight - window.innerHeight;
    if (target != null && (target <= max + 1 || tries >= 20)) {
      window.scrollTo(0, Math.min(target, Math.max(0, max)));
      return;
    }
    if (tries++ < 20) setTimeout(tick, 50);
  };
  tick();
}

/**
 * Scroll behaviour for the single page app:
 *  - a new page (link click) always opens at the top
 *  - a link with #anchor scrolls to that section
 *  - Back and Forward return to where you were on that page
 * The browser's own restoration is switched off: it fires before our content
 * loads and lands shoppers in the middle of a page.
 */
export default function ScrollManager() {
  const location = useLocation();
  const navType = useNavigationType();
  const current = useRef({ key: location.key, y: 0 });
  const firstRender = useRef(true);

  // One listener for the whole session keeps the latest position.
  useEffect(() => {
    if ("scrollRestoration" in window.history) window.history.scrollRestoration = "manual";
    const onScroll = () => { current.current.y = window.scrollY; };
    const onHide = () => savePosition(current.current.key, current.current.y);
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("pagehide", onHide);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("pagehide", onHide);
    };
  }, []);

  useLayoutEffect(() => {
    // Save where the reader was on the page they just left, before moving.
    if (current.current.key !== location.key) savePosition(current.current.key, current.current.y);
    current.current = { key: location.key, y: 0 };
    const isFirst = firstRender.current;
    firstRender.current = false;

    if (location.hash) {
      const id = decodeURIComponent(location.hash.slice(1));
      scrollWhenReady(() => {
        const el = document.getElementById(id);
        return el ? el.getBoundingClientRect().top + window.scrollY - 70 : null;
      });
      return;
    }
    // A fresh load or refresh also reports POP; always open those at the top.
    if (navType === "POP" && !isFirst) {
      const saved = readPositions()[location.key];
      if (typeof saved === "number" && saved > 0) { scrollWhenReady(() => saved); return; }
    }
    window.scrollTo(0, 0);
  }, [location.key, location.hash, navType]);

  return null;
}
